import { AddButton, Button, CloseButton, IconButton, SaveButton } from '@ui/shared/Button';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import {
  closePopup,
  openPopup,
} from '../../../../data/providers/popupsProvider';
import { userAsPromise } from '../../../../data/providers/userProvider';
import { IAthlete, IGroup, ITeam, IUser } from '../../../../data/types';
import {
  parseExternalProperties,
  saveAthletes,
} from '../../../../data/writers/athletes';
import { useEntities } from '../../../../hooks/useEntities';
import { CardBody } from '../../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../../shared/card/CardHeader';
import { AthleteMerger } from '../../../shared/form/AthleteMerger';
import { LinearLoading } from '../../../shared/loading/LinearLoading';
import { PopupCard, PopupCardContent } from '../../../shared/popups/PopupCard';
import { AthleteForm } from './AthleteForm';
import {
  ErrorBanner,
  IErrorState,
  useDuplicateLookup,
  useErrorLookup,
  useFormError,
} from './validation';

interface IPopupProps {
  closeDialog: () => void;
  athlete: IAthlete;
}

const Popup = (props: IPopupProps): React.ReactElement => {
  const [athlete, setAthlete] = React.useState<IAthlete>(props.athlete);
  const [selectedGroups, setSelectedGroups] = React.useState<IGroup[]>([]);
  const [selectedTeams, setSelectedTeams] = React.useState<ITeam[]>([]);
  const [saving, setSaving] = React.useState<boolean>(false);
  const [user, setUser] = React.useState<IUser>();
  const [error, setError] = React.useState<IErrorState>({ active: false });
  const [isArchiving, setIsArchiving] = React.useState<boolean>(false);

  /// subscribe to entities
  const { athletes, teams, groups, loading } = useEntities();

  /**
   * @description get the org id
   */
  React.useEffect((): void => {
    userAsPromise().then(setUser);
  }, []);

  /**
   * @description set the selected groups and teams based on the supplied athlete
   */
  React.useEffect((): void => {
    if (props.athlete && !loading) {
      const mSelectedTeams = teams.filter((t: ITeam): boolean =>
        athlete?.teams?.includes(t.id)
      );
      const mSelectedGroups = groups.filter((g: IGroup): boolean =>
        athlete?.groups?.includes(g.id)
      );
      setSelectedGroups(mSelectedGroups);
      setSelectedTeams(mSelectedTeams);
    }
  }, [loading]);

  /**
   * @description set the selected groups and teams based on the supplied athlete
   */
  React.useEffect((): void => {
    if (!loading) {
      const mSelectedGroups = groups.filter((g: IGroup): boolean =>
        athlete?.groups?.includes(g.id)
      );
      const mSelectedTeams = teams.filter((t: ITeam): boolean =>
        athlete?.teams?.includes(t.id)
      );
      setSelectedGroups(mSelectedGroups);
      setSelectedTeams(mSelectedTeams);
    }
  }, [loading]);

  /**
   * @description handles changes to the athlete
   * @param name
   * @param value
   */
  const onChange = (name: string, value: string): void => {
    const externalProperties = parseExternalProperties({ [name]: value });
    let changedAthlete: IAthlete;

    if (externalProperties) {
      changedAthlete = {
        ...athlete,
        external: { ...externalProperties },
      };
    } else {
      changedAthlete = {
        ...athlete,
        [name]: value,
      };
    }
    setAthlete(changedAthlete);
  };

  /**
   * @description handles image change
   * @param mAthlete
   */
  const onImageChange = (mAthlete: Partial<IAthlete>): void => {
    setAthlete(mAthlete as IAthlete);
  };

  /**
   * @description handles the user saving the athlete(s)
   */
  const saveAthlete = (): void => {
    setSaving(true);
    setError({ active: false });

    const athleteToWrite = {
      ...athlete,
      teams: selectedTeams.map((t: ITeam): string => t.id),
      groups: selectedGroups.map((g: IGroup): string => g.id),
    };
    saveAthletes([athleteToWrite])
      .then((): void => props.closeDialog())
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .catch((e: any): void => {
        setSaving(false);
        setError({
          active: true,
          message: e?.details?.reason ?? e.message,
          items: e?.details?.invalidAthletes ?? [],
        });
      });
  };

  const duplicateLookup = useDuplicateLookup(athletes);

  const errorLookup = useErrorLookup(athlete, duplicateLookup);

  /**
   * @description handles the selection/de-selection of a team
   * @param id
   * @param checked
   */
  const teamSelectionHandler = (team: ITeam, checked: boolean): void => {
    let mSelectedTeams = [...selectedTeams];
    if (checked) {
      mSelectedTeams.push(team);
    } else {
      mSelectedTeams = mSelectedTeams.filter(
        (t: ITeam): boolean => t.id !== team.id
      );
    }
    setSelectedTeams(mSelectedTeams);
  };

  /**
   * @description handles the selection/de-selection of a group
   * @param id
   * @param checked
   */
  const groupSelectionHandler = (group: IGroup, checked: boolean): void => {
    let mSelectedGroups = [...selectedGroups];
    if (checked) {
      mSelectedGroups.push(group);
    } else {
      mSelectedGroups = mSelectedGroups.filter(
        (g: IGroup): boolean => g.id !== group.id
      );
    }
    setSelectedGroups(mSelectedGroups);
  };

  /**
   * @description handles the deactivation of an athlete
   */
  const archiveAthlete = async (): Promise<void> => {
    setIsArchiving(true);
    try {
      await saveAthletes([
        {
          ...(athlete as IAthlete),
          active: false,
        },
      ]);
      props.closeDialog();
    } catch (err) {
      setError({
        active: true,
        message: err?.details?.reason ?? err.message,
        items: err?.details?.invalidAthletes ?? [],
      });
    } finally {
      setIsArchiving(false);
    }
  };

  /**
   * @description when the selected teams change, remove any selected groups that are not members
   *	of the currently selected teams
   */
  React.useEffect((): void => {
    const teamIds = selectedTeams.map((t: ITeam): string => t.id);
    const filteredGroups = selectedGroups.filter((group: IGroup): boolean =>
      group.teams?.some((teamId: string): boolean => teamIds.includes(teamId))
    );
    if (filteredGroups.length !== selectedGroups.length) {
      setSelectedGroups(filteredGroups);
    }
  }, [selectedTeams.length]);

  const hasFormError = useFormError({
    user,
    athlete,
    selectedTeams,
  });
  /**
   * @description the list of athletes that are available to select because
   *	they are members of at least one selected team
   */
  const groupsAvailableToSelect: IGroup[] = React.useMemo((): IAthlete[] => {
    return groups.filter((g: IGroup): boolean =>
      g.teams?.some((id: string): boolean =>
        selectedTeams.map((t: ITeam): string => t.id).includes(id)
      )
    );
  }, [groups?.length, selectedTeams]);

  /**
   * @description selects all the available athletes
   */
  const selectAllGroups = (): void =>
    setSelectedGroups(groupsAvailableToSelect);
  /**
   * @description clears the currently selected athletes
   */
  const clearAllGroups = (): void => setSelectedGroups([]);

  /**
   * @description selects all the available teams
   */
  const selectAllTeams = (): void => setSelectedTeams(teams);

  /**
   * @description clears the currently selected teams
   */
  const clearAllTeams = (): void => setSelectedTeams([]);

  return (
    <PopupCard onClose={props.closeDialog} size='large' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h5>Edit Athlete</h5>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.closeDialog} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          {saving && <LinearLoading />}
          {error.active && <ErrorBanner message={error?.message} />}
          <AthleteForm
            athlete={athlete as Partial<IAthlete>}
            teams={teams}
            athletes={athletes}
            selectedTeams={selectedTeams}
            selectedGroups={selectedGroups}
            teamSelectionHandler={teamSelectionHandler}
            groups={groupsAvailableToSelect}
            groupSelectionHandler={groupSelectionHandler}
            onImageChange={onImageChange}
            errorLookup={errorLookup}
            clearAllTeams={clearAllTeams}
            selectAllTeams={selectAllTeams}
            clearAllGroups={clearAllGroups}
            selectAllGroups={selectAllGroups}
            onChange={onChange}
            enforceUniqueAthleteId={user?.orgOptions?.enforceUniqueAthleteId}
          />
        </CardBody>
        <CardFooter surface={8}>
          <CardFooterLeft>
            <Button
              title='Archive Athlete'
              border={'none'}
              color={'danger'}
              flavor={'none'}
              onClick={archiveAthlete}
              disabled={isArchiving}
             />
          </CardFooterLeft>
          <CardFooterRight
            style={{ display: 'flex', justifyContent: 'flex-end', flex: 1 }}
          >
            <AthleteMerger
              athlete={athlete}
              level={1}
              onClose={props.closeDialog}
            />
            <SaveButton
              type='submit'
              onClick={saveAthlete}
              disabled={saving || hasFormError}
            />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </PopupCard>
  );
};
interface IProps extends RouteComponentProps {
  athlete: IAthlete;
}

const ManageAthletePopup = withRouter((props: IProps): React.ReactElement => {
  const openDialog = (e: React.MouseEvent<HTMLElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    openPopup(
      <Popup closeDialog={closePopup(props.history)} athlete={props.athlete} />,
      props.history,
      `athlete-edit-${props.athlete.id}`
    );
  };

  return <IconButton onClick={openDialog} icon='edit' title='Edit Athlete' />;
});

export { ManageAthletePopup };
